import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import * as ramda from 'ramda';

import {Avatar} from '../avatar/avatar';
import {Checkbox} from '../checkbox/checkbox';

import styles from './selectable-list-row.module.css';

export type Model = {
    id: string;
    readonly type: string;
    name: string;
    subtitle?: string;
    thirdLine?: string;
    initials?: string;
    avatarUrl?: string;
    isEnabled?: boolean;
    numSelected?: number;
    icon?: React.ReactElement<any>;
    // Tacked on for disabled styles and tooltips
    tooltipText?: string;
    isDisabled?: boolean;
    isLoggedInUser?: boolean;
};

type Props = {
    item: Model;
    hasCheckboxes?: boolean;
    isBorderless?: boolean;
    isDisabled?: boolean;
    isChecked: boolean;
    onSelect: (item: Model, isCurrentlySelected: boolean) => void;
    children?: React.ReactNode;
    icon?: React.ReactElement<any>;
    iconRight?: React.ReactElement<any>;
    tooltipText?: string;
};

export class SelectableListRow extends React.Component<Props> {
    componentDidMount() {
        if (this.props.tooltipText) {
            ReactTooltip.rebuild();
        }
    }

    render() {
        const {item, isChecked, hasCheckboxes, isBorderless, icon, iconRight, tooltipText} =
            this.props;

        // Users and teams use the isEnabled property, but we want a different
        // display style for unusable items.
        const isDisabled =
            this.props.isDisabled || (!ramda.isNil(item.isEnabled) && !item.isEnabled);
        const disabledStyle = this.props.isDisabled
            ? styles['name-disabled--ghost']
            : styles['name-disabled--line-through'];
        const classes = classnames(styles['item-container'], {
            [styles['item-container-selected']]: isChecked && !hasCheckboxes,
            [styles['item-container-borderless']]: isBorderless,
            [styles['item-container-disabled']]: this.props.isDisabled,
        });

        return (
            <div key={item.id} data-tip={tooltipText} className={styles['selectable-list-row']}>
                <div onClick={this.handleSelect} className={classes}>
                    {hasCheckboxes ? (
                        <div className={styles['checkbox-container']}>
                            <Checkbox
                                checked={isChecked}
                                onClick={() => {}}
                                disabled={this.props.isDisabled}
                            />
                        </div>
                    ) : undefined}
                    {item.type === 'teams' ? <div className={styles['icon-teams']} /> : null}
                    {item.type === 'users' ||
                    item.type === 'leadStatus' ||
                    item.type === 'contacts' ||
                    item.type === 'accounts' ? (
                        <div className={styles.icon}>
                            <Avatar
                                size={20}
                                avatarUrl={item.avatarUrl}
                                initials={item.initials}
                                type={item.type}
                            />
                        </div>
                    ) : null}
                    {icon ? <div className={styles.icon}>{icon}</div> : undefined}
                    {getDisplay({
                        name: item.name,
                        subtitle: item.subtitle,
                        thirdLine: item.thirdLine,
                        isDisabled,
                        disabledStyle,
                        isLoggedInUser: item.isLoggedInUser,
                    })}
                    {item.numSelected && item.numSelected > 0 ? (
                        <div className={styles['num-selected']}>{item.numSelected}</div>
                    ) : undefined}
                    {iconRight ? (
                        <div className={styles['icon-right']}>{iconRight}</div>
                    ) : undefined}
                </div>
                {this.props.children}
            </div>
        );
    }

    handleSelect = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        if (!this.props.isDisabled) {
            this.props.onSelect(this.props.item, this.props.isChecked);
        }
    };
}

const getDisplay = (input: {
    name: string;
    isDisabled: boolean;
    disabledStyle: string;
    subtitle?: string;
    thirdLine?: React.ReactNode;
    isLoggedInUser?: boolean;
}) => {
    const {name, subtitle, thirdLine, isDisabled, disabledStyle, isLoggedInUser} = input;

    return (
        <div className={classnames('flex flex-dir-col', isDisabled ? disabledStyle : styles.name)}>
            <div className={styles.name}>
                <span>{name}</span>
                {isLoggedInUser && <span className={styles['you-text']}>&nbsp;(You)</span>}
            </div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            {thirdLine}
        </div>
    );
};
